import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSophos, type SophosAlert, type SophosFirewall, type SophosRenewal, type SophosTenant } from '@/api/network';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { CategoryBar } from '@/components/dashboard/charts';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { HBar, count, oneHue } from '@/components/rmm/RmmDashboardKit';
import {
  Chip, CompanyCell, Empty, ListPage, Notice, PageLoading, Panel, Th, UnmappedNotice, subscriptionChip, tableWrap, theadCls, useListState, type FilterDef,
} from '@/components/network/NetworkKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, networkView } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Network → Sophos. Firewalls only (Synfo has no Sophos endpoints). Everything here is Sophos
 * Central data: connection, SFOS firmware, Xstream/other subscriptions, Central alerts and the
 * account health check's firewall auto-backup check. Kept separate from UniFi on purpose.
 */

const S = ROUTES.network.sophos;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Sophos Central returns some alert text HTML-encoded ("CENTRAL &amp; SDWAN"). */
const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'" };
const decodeEntities = (s: string) => s.replace(/&(amp|lt|gt|quot|#39|apos);/g, (_, e: string) => ENTITIES[e]);

function SophosProgress({ p }: { p: { running: boolean; done: number; total: number; finishedAt: string | null } }) {
  if (p.finishedAt) return null;
  return <div className="mt-3"><Notice>Reading Sophos Central{p.total ? ` — ${p.done} of ${p.total} tenants` : ''} (about a minute after a restart, then hourly).</Notice></div>;
}

// =================== Dashboard ===================

export function SophosDashboardPage() {
  const { data: res, isLoading, error, isFetching } = useSophos({ scoped: true });
  const navigate = useNavigate();
  const d = res?.data;
  const k = d?.kpis;

  // Subscription renewals per month, next 12 months (one hue — it's a magnitude by period).
  const byMonth = useMemo(() => {
    const months: { id: string; label: string; value: number }[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const m = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const key = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
      // Month only ("Oct"), year on January: twelve "Oct 26" labels overlap on a chart this narrow.
      months.push({ id: key, label: m.getMonth() === 0 ? `Jan ${String(m.getFullYear()).slice(2)}` : MONTHS[m.getMonth()], value: 0 });
    }
    for (const r of d?.renewals ?? []) {
      const mo = months.find((m) => r.end?.startsWith(m.id));
      if (mo) mo.value++;
    }
    return months;
  }, [d?.renewals]);

  const attention = (d?.firewalls ?? []).filter((f) => !f.connected || f.subscriptionState === 'expired' || f.subscriptionState === 'expiring' || f.openAlerts > 0)
    .sort((a, b) => Number(a.connected) - Number(b.connected) || (a.subscriptionDaysLeft ?? 9e9) - (b.subscriptionDaysLeft ?? 9e9));

  return (
    <DesktopOnly title="Network is available on desktop" message="Open Cosmos on a larger screen to view the network.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader title="Sophos firewalls" isFetching={isFetching}
          subtitle={d?.progress.finishedAt ? <>Sophos Central · read {fmtRelative(d.progress.finishedAt)} (hourly)</> : 'Sophos Central'} />
        <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="Sophos Central" />
        {d && k && (
          <>
            <SophosProgress p={d.progress} />
            <div className="mt-3"><UnmappedNotice count={k.unmapped} what={`Sophos tenant${k.unmapped === 1 ? '' : 's'}`} /></div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 min-[1400px]:grid-cols-6">
              <StatCard label="Firewalls" value={k.firewalls} sub={`${k.connected} connected · ${k.tenants} tenants`} onClick={() => navigate(S.firewalls)} />
              <StatCard label="Disconnected" value={k.disconnected} sub="not talking to Central" tone={k.disconnected ? 'destructive' : 'success'} onClick={() => navigate(networkView(S.firewalls, { show: 'disconnected' }))} />
              <StatCard label="Xstream expired" value={k.subscriptionsExpired} sub={`${k.subscriptionsExpiring} expiring within ${d.warnDays} days`} tone={k.subscriptionsExpired ? 'destructive' : k.subscriptionsExpiring ? 'warning' : 'success'} onClick={() => navigate(networkView(S.firewalls, { show: 'subscription' }))} />
              <StatCard label="Behind on firmware" value={k.behindFirmware} sub={d.latestMajor ? `not on ${d.latestMajor}` : 'firmware unknown'} tone={k.behindFirmware ? 'warning' : 'success'} onClick={() => navigate(networkView(S.firewalls, { show: 'firmware' }))} />
              <StatCard label="Open alerts" value={k.alerts} sub="in Sophos Central" tone={k.alerts ? 'warning' : 'success'} onClick={() => navigate(S.alerts)} />
              <StatCard label="Config backup" value={k.backupNotCompliant} sub="tenants off schedule" tone={k.backupNotCompliant ? 'warning' : 'success'} onClick={() => navigate(S.tenants)} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <ChartCard title="Renewals · next 12 months" action={<Link to={S.subscriptions} className="text-xs text-muted-foreground hover:underline">All →</Link>}>
                <CategoryBar data={oneHue(byMonth)} valueFormatter={count} name="Renewals" height={200} onSelect={() => navigate(S.subscriptions)} />
              </ChartCard>
              <ChartCard title="SFOS firmware">
                <HBar data={oneHue(d.charts.firmware)} name="Firewalls" onSelect={() => navigate(networkView(S.firewalls, { show: 'firmware' }))} />
              </ChartCard>
              <ChartCard title="Models">
                <HBar data={oneHue(d.charts.models)} name="Firewalls" />
              </ChartCard>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 min-[1400px]:grid-cols-2">
              <Panel title={`Needs attention · ${attention.length}`} more={{ to: S.firewalls, label: 'All firewalls' }}>
                {attention.length === 0 ? <div className="px-4 py-6 text-center text-xs text-success">Every firewall connected and subscribed.</div> : (
                  <table className="w-full text-sm"><tbody className="divide-y divide-divider">
                    {attention.map((f) => (
                      <tr key={f.id}>
                        <td className="px-4 py-2"><div className="font-medium text-foreground">{f.hostname}</div><div className="whitespace-nowrap text-[11px] text-muted-foreground">{f.model} · {f.serial}</div></td>
                        <td className="max-w-[180px] px-4 py-2 text-xs"><CompanyCell companyId={f.companyId} companyName={f.companyName} fallback={f.tenantName} /></td>
                        <td className="px-4 py-2 text-right"><div className="flex flex-wrap justify-end gap-1">
                          {!f.connected && <Chip tone="bad">Disconnected</Chip>}
                          {(f.subscriptionState === 'expired' || f.subscriptionState === 'expiring') && subscriptionChip(f.subscriptionState, f.subscriptionDaysLeft, f.subscriptionEnd)}
                          {f.openAlerts > 0 && <Chip tone="warn">{f.openAlerts} alert{f.openAlerts === 1 ? '' : 's'}</Chip>}
                        </div></td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </Panel>
              <Panel title={`Alert types · ${d.alerts.length}`} more={{ to: S.alerts, label: 'All alerts' }}>
                {d.charts.alertTypes.length === 0 ? <div className="px-4 py-6 text-center text-xs text-success">No open alerts.</div> : (
                  <ul className="divide-y divide-divider text-sm">
                    {d.charts.alertTypes.map((a) => <li key={a.id} className="flex justify-between gap-3 px-4 py-2"><span className="min-w-0 truncate text-foreground" title={decodeEntities(a.label)}>{decodeEntities(a.label)}</span><span className="tabular-nums text-muted-foreground">{a.value}</span></li>)}
                  </ul>
                )}
              </Panel>
            </div>
            {d.unlisted.length > 0 && (
              <p className="mt-3 text-[11px] text-muted-foreground">
                {d.unlisted.length} licensed firewall serial{d.unlisted.length === 1 ? ' is' : 's are'} not registered to any tenant in Central (sold, replaced or never onboarded) — see <Link to={S.subscriptions} className="hover:underline">Subscriptions</Link>.
              </p>
            )}
          </>
        )}
      </div>
    </DesktopOnly>
  );
}

// =================== Firewalls ===================

export function SophosFirewallsPage() {
  const { data: res, isLoading, error, isFetching } = useSophos({ scoped: true });
  const d = res?.data;
  const filters = useMemo<FilterDef<SophosFirewall>[]>(() => [
    { id: 'all', label: 'All', test: () => true },
    { id: 'disconnected', label: 'Disconnected', test: (f) => !f.connected },
    { id: 'subscription', label: 'Xstream expired / expiring', test: (f) => f.subscriptionState === 'expired' || f.subscriptionState === 'expiring' },
    { id: 'firmware', label: `Not on ${d?.latestMajor ?? 'latest'}`, test: (f) => !!d?.latestMajor && !!f.firmwareMajor && f.firmwareMajor !== d.latestMajor },
    { id: 'alerts', label: 'With alerts', test: (f) => f.openAlerts > 0 },
    { id: 'unmapped', label: 'Unmapped', test: (f) => f.companyId == null },
  ], [d?.latestMajor]);
  const st = useListState('cosmos:network:sophos:firewalls', filters, d?.firewalls ?? [], (f) => `${f.hostname} ${f.serial} ${f.model} ${f.companyName ?? ''} ${f.tenantName ?? ''} ${f.externalIps.join(' ')}`);
  return (
    <ListPage title="Firewalls" subtitle="Sophos Central" count={d ? st.rows.length : null} isFetching={isFetching}
      search={{ q: st.q, setQ: st.setQ, placeholder: 'Hostname, serial, client or IP' }} filters={{ defs: filters, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="Sophos Central" />
      {d && (
        <div className={tableWrap}>
          <table className="w-full text-sm" style={{ minWidth: 1100 }}>
            <thead className={theadCls}><tr><Th>Firewall</Th><Th>Client</Th><Th>Central</Th><Th>Firmware</Th><Th>Xstream</Th><Th>Other licences</Th><Th>Last seen</Th><Th>WAN IP</Th><Th right>Alerts</Th></tr></thead>
            <tbody className="divide-y divide-divider">
              {st.rows.length === 0 && <Empty cols={9} />}
              {st.rows.map((f) => {
                const other = f.licences.filter((l) => l.kind === 'subscription' && !/^X-XS/.test(l.code ?? ''));
                return (
                  <tr key={f.id} className="align-top hover:bg-muted/30">
                    <td className="px-3 py-2"><div className="whitespace-nowrap font-medium text-foreground">{f.hostname}</div><div className="whitespace-nowrap text-[11px] text-muted-foreground">{f.model ?? '—'} · {f.serial}{f.ha && ' · HA'}</div></td>
                    <td className="max-w-[130px] px-3 py-2 text-xs"><CompanyCell companyId={f.companyId} companyName={f.companyName} fallback={f.tenantName} /></td>
                    <td className="px-3 py-2">{f.connected ? <Chip tone="good">Connected</Chip> : <Chip tone="bad" title={f.stateChangedAt ? `Since ${f.stateChangedAt}` : undefined}>Disconnected</Chip>}{f.suspended && <div className="mt-1"><Chip tone="warn">Suspended</Chip></div>}</td>
                    <td className={cn('px-3 py-2 text-xs tabular-nums', d.latestMajor && f.firmwareMajor !== d.latestMajor ? 'text-warning' : 'text-muted-foreground')} title={f.firmwareMajor ?? ''}>{f.firmware ?? '—'}</td>
                    <td className="px-3 py-2">{subscriptionChip(f.subscriptionState, f.subscriptionDaysLeft, f.subscriptionEnd)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{other.length ? other.map((l) => <div key={`${l.code}${l.end}`}>{l.name} · {l.end}</div>) : '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground" title={f.lastSeen ?? ''}>{f.lastSeen ? fmtRelative(f.lastSeen).replace(/^(about|almost|over) /, '') : '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-muted-foreground">{f.externalIps.length ? f.externalIps.map((ip) => <div key={ip}>{ip}</div>) : '—'}</td>
                    <td className={cn('px-3 py-2 text-right tabular-nums', f.openAlerts ? 'font-medium text-warning' : 'text-muted-foreground')}>{f.openAlerts ? <Link to={S.alerts} className="hover:underline">{f.openAlerts}</Link> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}

// =================== Subscriptions ===================

export function SophosSubscriptionsPage() {
  const { data: res, isLoading, error, isFetching } = useSophos({ scoped: true });
  const d = res?.data;
  const filters = useMemo<FilterDef<SophosRenewal>[]>(() => [
    { id: 'upcoming', label: 'Next 90 days', test: (r) => r.daysLeft != null && r.daysLeft >= 0 && r.daysLeft <= 90 },
    { id: 'expired', label: 'Expired', test: (r) => r.daysLeft != null && r.daysLeft < 0 },
    { id: 'all', label: 'All', test: () => true },
  ], []);
  const st = useListState('cosmos:network:sophos:subs', filters, d?.renewals ?? [], (r) => `${r.hostname} ${r.serial} ${r.name} ${r.companyName ?? ''} ${r.tenantName ?? ''}`);
  return (
    <ListPage title="Subscriptions" subtitle="Every term licence on every firewall (base licences are perpetual and not listed). Expired subscriptions stop protection updates."
      count={d ? st.rows.length : null} isFetching={isFetching} search={{ q: st.q, setQ: st.setQ, placeholder: 'Firewall, licence or client' }}
      filters={{ defs: filters, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="Sophos Central" />
      {d && (
        <>
          <div className={tableWrap}>
            <table className="w-full text-sm">
              <thead className={theadCls}><tr><Th>Ends</Th><Th>Licence</Th><Th>Firewall</Th><Th>Client</Th><Th>Started</Th></tr></thead>
              <tbody className="divide-y divide-divider">
                {st.rows.length === 0 && <Empty cols={5} />}
                {st.rows.map((r, i) => (
                  <tr key={`${r.serial}-${r.code}-${r.end}-${i}`} className="hover:bg-muted/30">
                    <td className="px-3 py-2">{subscriptionChip(r.daysLeft == null ? 'none' : r.daysLeft < 0 ? 'expired' : r.daysLeft <= d.warnDays ? 'expiring' : 'ok', r.daysLeft, r.end)}</td>
                    <td className="px-3 py-2 text-foreground">{r.name}</td>
                    <td className="px-3 py-2"><div className="text-foreground">{r.hostname}</div><div className="text-[11px] text-muted-foreground">{r.model} · {r.serial}</div></td>
                    <td className="max-w-[220px] px-3 py-2 text-xs"><CompanyCell companyId={r.companyId} companyName={r.companyName} fallback={r.tenantName} /></td>
                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{r.start ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.unlisted.length > 0 && (
            <section className="mt-4 rounded-lg border border-border bg-card shadow-sm">
              <div className="border-b border-divider px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Licensed but not in any tenant · {d.unlisted.length}</div>
              <table className="w-full text-sm"><tbody className="divide-y divide-divider">
                {d.unlisted.map((u) => (
                  <tr key={u.serial}>
                    <td className="px-4 py-2"><div className="text-foreground">{u.serial}</div><div className="text-[11px] text-muted-foreground">{u.model ?? '—'}</div></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{u.lastSeen ? `last seen ${fmtRelative(u.lastSeen)}` : 'never seen'}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{u.licences.filter((l) => l.kind === 'subscription').map((l) => `${l.name} → ${l.end}`).join(' · ') || '—'}</td>
                  </tr>
                ))}
              </tbody></table>
            </section>
          )}
        </>
      )}
    </ListPage>
  );
}

// =================== Alerts ===================

const SEV_TONE: Record<string, 'bad' | 'warn' | 'info' | 'muted'> = { high: 'bad', medium: 'warn', low: 'info' };

export function SophosAlertsPage() {
  const { data: res, isLoading, error, isFetching } = useSophos({ scoped: true });
  const d = res?.data;
  const filters = useMemo<FilterDef<SophosAlert>[]>(() => [
    { id: 'all', label: 'All', test: () => true },
    { id: 'high', label: 'High', test: (a) => a.severity === 'high' },
    { id: 'medium', label: 'Medium', test: (a) => a.severity === 'medium' },
    { id: 'low', label: 'Low', test: (a) => a.severity === 'low' },
  ], []);
  const st = useListState('cosmos:network:sophos:alerts', filters, d?.alerts ?? [], (a) => `${a.description} ${a.hostname ?? ''} ${a.serial ?? ''} ${a.companyName ?? ''} ${a.tenantName ?? ''}`);
  const rows = [...st.rows].sort((a, b) => (b.raisedAt ?? '').localeCompare(a.raisedAt ?? ''));
  return (
    <ListPage title="Alerts" subtitle="Open firewall alerts in Sophos Central (read-only — acknowledge them in Central)" count={d ? rows.length : null} isFetching={isFetching}
      search={{ q: st.q, setQ: st.setQ, placeholder: 'Alert, firewall or client' }} filters={{ defs: filters, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="Sophos Central" />
      {d && (
        <div className={tableWrap}>
          <table className="w-full text-sm">
            <thead className={theadCls}><tr><Th>Raised</Th><Th>Severity</Th><Th>Alert</Th><Th>Firewall</Th><Th>Client</Th></tr></thead>
            <tbody className="divide-y divide-divider">
              {rows.length === 0 && <Empty cols={5} text="No open alerts." />}
              {rows.map((a) => (
                <tr key={a.id} className="hover:bg-muted/30">
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{a.raisedAt ? fmtRelative(a.raisedAt) : '—'}</td>
                  <td className="px-3 py-2"><Chip tone={SEV_TONE[a.severity] ?? 'muted'}>{a.severity}</Chip></td>
                  <td className="px-3 py-2 text-foreground">{decodeEntities(a.description)}</td>
                  <td className="px-3 py-2 text-xs"><div className="whitespace-nowrap text-foreground">{a.hostname ?? '—'}</div><div className="whitespace-nowrap text-[11px] text-muted-foreground">{a.serial}</div></td>
                  <td className="max-w-[220px] px-3 py-2 text-xs"><CompanyCell companyId={a.companyId} companyName={a.companyName} fallback={a.tenantName} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}

// =================== Tenants ===================

export function SophosTenantsPage() {
  const { data: res, isLoading, error, isFetching } = useSophos({ scoped: true });
  const d = res?.data;
  const filters = useMemo<FilterDef<SophosTenant>[]>(() => [
    { id: 'all', label: 'All', test: () => true },
    { id: 'backup', label: 'Config backup not compliant', test: (t) => !!t.backup && (t.backup.score ?? 100) < 100 },
    { id: 'unmapped', label: 'Unmapped', test: (t) => t.companyId == null && !t.mappingIgnored },
    { id: 'errors', label: 'Read errors', test: (t) => !!t.error },
  ], []);
  const st = useListState('cosmos:network:sophos:tenants', filters, d?.tenants ?? [], (t) => `${t.name} ${t.contactDomain ?? ''} ${t.companyName ?? ''}`);
  return (
    <ListPage title="Sophos tenants" subtitle="Sophos Central customer tenants, their Autotask company and the health check's firewall auto-backup check"
      count={d ? st.rows.length : null} isFetching={isFetching} search={{ q: st.q, setQ: st.setQ, placeholder: 'Tenant, domain or client' }}
      filters={{ defs: filters, show: st.show, pick: st.pick, counts: st.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={res?.configured} vendor="Sophos Central" />
      {d && (
        <div className={tableWrap}>
          <table className="w-full text-sm">
            <thead className={theadCls}><tr><Th>Tenant</Th><Th>Client</Th><Th right>Firewalls</Th><Th>Config auto-backup</Th><Th>Billing</Th><Th>Read</Th></tr></thead>
            <tbody className="divide-y divide-divider">
              {st.rows.length === 0 && <Empty cols={6} />}
              {st.rows.map((t) => (
                <tr key={t.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2"><div className="font-medium text-foreground">{t.name}</div><div className="text-[11px] text-muted-foreground">{t.contactDomain ?? '—'}</div></td>
                  <td className="max-w-[220px] px-3 py-2 text-xs">{t.mappingIgnored ? <span className="italic text-muted-foreground">ignored</span> : <CompanyCell companyId={t.companyId} companyName={t.companyName} />}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.firewalls}</td>
                  <td className="px-3 py-2 text-xs">
                    {t.backup?.score == null ? <span className="text-muted-foreground">—</span>
                      : t.backup.score >= 100 ? <Chip tone="good">On schedule{t.backup.frequency ? ` (${t.backup.frequency})` : ''}</Chip>
                      : <Chip tone="warn">{t.backup.notOnRecommended ?? '?'} of {t.backup.total ?? '?'} off schedule</Chip>}
                  </td>
                  <td className="px-3 py-2 text-xs capitalize text-muted-foreground">{t.billingType === 'usage' ? 'MSP Flex (usage)' : t.billingType ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{t.error ? <span className="text-destructive" title={t.error}>error</span> : <span className="text-muted-foreground">{t.fetchedAt ? fmtRelative(t.fetchedAt) : '—'}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}
